import { Component, type ReactNode } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import FirmaNavbar from '@/components/FirmaNavbar'
import ProtectedRoute, { AdminRoute, SuperAdminRoute } from '@/components/ProtectedRoute'
import FirmaLoginGate from '@/components/FirmaLoginGate'
import { FirmaProvider } from '@/contexts/FirmaContext'
import { RightPanelProvider } from '@/contexts/RightPanelContext'
import { SidebarProvider } from '@/contexts/SidebarContext'
import SearchPage from '@/pages/SearchPage'
import FirmaPage from '@/pages/FirmaPage'
import TeilnehmerDetailPage from '@/pages/TeilnehmerDetailPage'
import TeilnehmerAnlegen from '@/pages/TeilnehmerAnlegen'
import LoginPage from '@/pages/LoginPage'
import FirmaSelectPage from '@/pages/FirmaSelectPage'
import InvitePage from '@/pages/InvitePage'
import AdminPage from '@/pages/AdminPage'
import AnmeldeportalPage from '@/pages/AnmeldeportalPage'
import BlackboardPage from '@/pages/BlackboardPage'
import SuperAdminPage from '@/pages/SuperAdminPage'
import NotificationSettingsPage from '@/pages/NotificationSettingsPage'
import FreigabeConfirmPage from '@/pages/FreigabeConfirmPage'
import KostenuebernahmenListPage from '@/pages/SuperAdmin/KostenuebernahmenListPage'
import KostenuebernahmenDetailPage from '@/pages/SuperAdmin/KostenuebernahmenDetailPage'
import FirmenOhneAdminPage from '@/pages/SuperAdmin/FirmenOhneAdminPage'
import AdminApprovalOverduePage from '@/pages/SuperAdmin/AdminApprovalOverduePage'
import ErklaerungWizardPage from '@/pages/public/ErklaerungWizardPage'
import ZuzahlerSetupPage from '@/pages/Zuzahler/SetupPage'
import ZuzahlerTasksListPage from '@/pages/Zuzahler/TasksListPage'
import ZuzahlerTaskDetailPage from '@/pages/Zuzahler/TaskDetailPage'
import ZuzahlerApprovalsPage from '@/pages/Firma/ZuzahlerApprovalsPage'
import { useAuth } from '@/contexts/AuthContext'

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, fontFamily: 'monospace' }}>
          <h2 style={{ color: 'red' }}>React Error</h2>
          <pre style={{ whiteSpace: 'pre-wrap', background: '#fee', padding: 16, borderRadius: 8 }}>
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 16, padding: '8px 16px' }}>
            Retry
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

/** Wrapper für Firma-Routen: FirmaProvider + Navbar + Login-Gate + Content */
function FirmaLayout({ children }: { children: React.ReactNode }) {
  return (
    <FirmaProvider>
      <SidebarProvider>
        <RightPanelProvider>
          <FirmaNavbar />
          <FirmaLoginGate>{children}</FirmaLoginGate>
        </RightPanelProvider>
      </SidebarProvider>
    </FirmaProvider>
  )
}

/** Startseite: Superadmin → Suche, Firma-User → eigene Firma */
function HomeRedirect() {
  const { user, firmen } = useAuth()
  if (user?.Role === 'superadmin') {
    return <Navigate to="/select-firma" replace state={{ firmen }} />
  }
  if (firmen.length === 1) {
    return <Navigate to={`/firma/${firmen[0].token}`} replace />
  }
  return <Navigate to="/select-firma" replace state={{ firmen }} />
}

export default function App() {
  return (
    <ErrorBoundary>
    <div className="flex flex-col h-screen overflow-hidden bg-[#F7F7F7]">
      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto relative">
        <Routes>
          {/* Öffentliche Portal-Routen */}
          <Route path="/portal/login" element={<LoginPage />} />
          <Route path="/portal/invite" element={<InvitePage />} />
          <Route path="/select-firma" element={<FirmaSelectPage />} />
          <Route path="/freigabe/:token" element={<FreigabeConfirmPage />} />
          <Route path="/erklaerung/:token" element={<ErklaerungWizardPage />} />
          <Route path="/zuzahler/setup/:token" element={<ZuzahlerSetupPage />} />

          {/* Firma-Routen (FirmaLayout = FirmaProvider + Navbar + Login-Gate) */}
          <Route path="/firma/:firmaToken" element={<FirmaLayout><FirmaPage /></FirmaLayout>} />
          <Route path="/firma/:firmaToken/teilnehmer-anlegen" element={<FirmaLayout><TeilnehmerAnlegen /></FirmaLayout>} />
          <Route path="/firma/:firmaToken/teilnehmer/:personenId" element={<FirmaLayout><TeilnehmerDetailPage /></FirmaLayout>} />
          <Route path="/firma/:firmaToken/teilnehmer/:personenId/termine/:stnId" element={<FirmaLayout><TeilnehmerDetailPage /></FirmaLayout>} />

          {/* Geschützte Routen */}
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/suche" element={<><FirmaNavbar /><SearchPage /></>} />

            {/* Admin-Bereich */}
            <Route path="/admin" element={<AdminRoute><FirmaNavbar /><AdminPage /></AdminRoute>} />

            {/* Anmeldeportal (nur Superadmin) */}
            <Route path="/anmeldeportal" element={<SuperAdminRoute><FirmaNavbar /><AnmeldeportalPage /></SuperAdminRoute>} />

            {/* Blackboard-Verwaltung (Superadmin) */}
            <Route path="/blackboard" element={<SuperAdminRoute><FirmaNavbar /><BlackboardPage /></SuperAdminRoute>} />

            {/* Superadmin-Cockpit (cross-firma) */}
            <Route path="/superadmin" element={<SuperAdminRoute><FirmaNavbar /><SuperAdminPage /></SuperAdminRoute>} />

            {/* Zuzahler-Erklärungen (Superadmin) */}
            <Route path="/superadmin/kostenuebernahmen" element={<SuperAdminRoute><FirmaNavbar /><KostenuebernahmenListPage /></SuperAdminRoute>} />
            <Route path="/superadmin/kostenuebernahmen/firmen-ohne-admin" element={<SuperAdminRoute><FirmaNavbar /><FirmenOhneAdminPage /></SuperAdminRoute>} />
            <Route path="/superadmin/kostenuebernahmen/admin-approval-overdue" element={<SuperAdminRoute><FirmaNavbar /><AdminApprovalOverduePage /></SuperAdminRoute>} />
            <Route path="/superadmin/kostenuebernahmen/:id" element={<SuperAdminRoute><FirmaNavbar /><KostenuebernahmenDetailPage /></SuperAdminRoute>} />

            {/* Zuzahler-Approvals (Phase G — Case-2: Firma-Admin bestaetigt neuen AP) */}
            <Route path="/firma/:firmaId/approvals" element={<><FirmaNavbar /><ZuzahlerApprovalsPage /></>} />

            {/* Zuzahler-Tasks (Phase H — eingeloggter AP signiert KÜ im Portal) */}
            <Route path="/zuzahler/tasks" element={<><FirmaNavbar /><ZuzahlerTasksListPage /></>} />
            <Route path="/zuzahler/tasks/:id" element={<><FirmaNavbar /><ZuzahlerTaskDetailPage /></>} />

            {/* Benachrichtigungs-Einstellungen (alle eingeloggten User) */}
            <Route path="/einstellungen/benachrichtigungen" element={<><FirmaNavbar /><NotificationSettingsPage /></>} />

          </Route>
        </Routes>
      </main>
    </div>
    </ErrorBoundary>
  )
}
